import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type {
  BatchPaymentItemDto,
  TransactionStatus,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { getLocalBankAccountNumberFromIBAN } from "Utils/AccountUtils";
import { formatCurrency } from "Utils/CurrencyUtils";

const RESOURCES = Resources.Banking.BatchPayments;
const COL_RESOURCES = Resources.Banking.BatchPayments.Columns;
const TX_STATUS_RESOURCES = Resources.Transactions.TransactionStatus;

const STATUS_TO_COLOR: Partial<Record<TransactionStatus, string>> = {
  Success: "success.main",
  Fail: "error.main",
};

const getStatusColor = (status?: TransactionStatus | null): string =>
  (status && STATUS_TO_COLOR[status]) || "text.secondary";

type Props = {
  items: BatchPaymentItemDto[];
};

export const PaymentItemsTable = ({ items }: Props) => {
  const { t } = useResource();

  return (
    <Stack gap={1}>
      <Typography variant="subtitle1" fontWeight={600}>
        {t(RESOURCES.PaymentsTable)} ({items.length})
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t(COL_RESOURCES.Recipient)}</TableCell>
              <TableCell>{t(COL_RESOURCES.IBAN)}</TableCell>
              <TableCell>{t(COL_RESOURCES.VS)}</TableCell>
              <TableCell align="right">{t(COL_RESOURCES.Amount)}</TableCell>
              <TableCell>{t(COL_RESOURCES.Status)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <PaymentItemRow key={index} item={item} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

const PaymentItemRow = ({ item }: { item: BatchPaymentItemDto }) => {
  const { t } = useResource();
  const {
    creditorName,
    creditorIBAN,
    paymentReference,
    amount,
    currencyCode,
    transactionStatus,
  } = item;

  return (
    <TableRow>
      <TableCell>{creditorName ?? "-"}</TableCell>
      <TableCell>{getLocalBankAccountNumberFromIBAN(creditorIBAN)}</TableCell>
      <TableCell>{paymentReference ?? "-"}</TableCell>
      <TableCell align="right">
        {formatCurrency({ amount, currencyCode })}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color={getStatusColor(transactionStatus)}>
          {transactionStatus ? t(TX_STATUS_RESOURCES[transactionStatus]) : "-"}
        </Typography>
      </TableCell>
    </TableRow>
  );
};
